/**
 * Verify email page — /verify-email?token=…
 * Consumes the verification token on load, then either navigates the user
 * into the app (fresh session) or shows an error with a retry link.
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, Mail } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { setToken } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level guard: survives React StrictMode double-mount and prevents
// the verification endpoint from being called twice for the same token.
const inFlight = new Set();
const results = new Map(); // token → { ok, data | error }

export const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [state, setState] = useState("loading"); // loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!token) {
      setState("error");
      setErrorMsg("Ongeldige link — geen token gevonden");
      return;
    }
    // If a prior mount already resolved this token, reuse the outcome.
    if (results.has(token)) {
      const r = results.get(token);
      if (r.ok) {
        setToken(r.data.session_token);
        setState("success");
        setTimeout(() => navigate("/overview", { replace: true }), 1400);
      } else {
        setState("error");
        setErrorMsg(r.error);
      }
      return;
    }
    if (inFlight.has(token)) return; // already fetching from another mount
    inFlight.add(token);
    (async () => {
      try {
        const res = await axios.post(`${API}/auth/verify-email`, { token });
        results.set(token, { ok: true, data: res.data });
        setToken(res.data.session_token);
        setState("success");
        setTimeout(() => navigate("/overview", { replace: true }), 1400);
      } catch (err) {
        const msg = err.response?.data?.detail || "Verificatie mislukt";
        results.set(token, { ok: false, error: msg });
        setState("error");
        setErrorMsg(msg);
      } finally {
        inFlight.delete(token);
      }
    })();
  }, [token, navigate]);

  return (
    <div
      data-testid="verify-email-page"
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "var(--chalk-50, #F7F5EE)" }}
    >
      <Card className="w-full max-w-md shadow-lg border-slate-200">
        <CardHeader className="text-center space-y-3">
          <div className="mx-auto w-14 h-14 rounded-full border flex items-center justify-center"
               style={
                 state === "success"
                   ? { background: "#ECFDF5", borderColor: "#A7F3D0" }
                   : state === "error"
                   ? { background: "#FEF2F2", borderColor: "#FECACA" }
                   : { background: "#F1F5F9", borderColor: "#CBD5E1" }
               }>
            {state === "loading" && <Loader2 className="w-7 h-7 text-slate-500 animate-spin" />}
            {state === "success" && <CheckCircle2 className="w-7 h-7 text-emerald-600" />}
            {state === "error" && <XCircle className="w-7 h-7 text-rose-600" />}
          </div>
          <CardTitle className="text-2xl font-bold" style={{ fontFamily: "var(--sportly-font-display, serif)" }}>
            {state === "loading" && "Bevestigen..."}
            {state === "success" && "E-mail bevestigd!"}
            {state === "error" && "Bevestiging mislukt"}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {state === "loading" && (
            <p className="text-sm text-slate-600" data-testid="verify-loading">
              We ronden je account nu af...
            </p>
          )}
          {state === "success" && (
            <p className="text-sm text-slate-600" data-testid="verify-success">
              Je account is aangemaakt. Je wordt automatisch doorgestuurd naar je dashboard...
            </p>
          )}
          {state === "error" && (
            <>
              <p className="text-sm text-rose-600" data-testid="verify-error-msg">{errorMsg}</p>
              <div className="pt-2 space-y-2">
                <Link to="/abonnementen">
                  <Button variant="outline" className="w-full inline-flex items-center gap-2" data-testid="verify-retry-btn">
                    <Mail className="w-4 h-4" /> Opnieuw registreren
                  </Button>
                </Link>
                <Link to="/login" className="text-xs text-slate-500 hover:text-slate-700 inline-block">
                  Toch al een account? Inloggen
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
